import {
  Badge,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";

import type { HubChromeScriptGroupSentItem } from "./hub-chrome.types.js";

const TOOLTIP_DELAY_MS = 300;
const MAX_LISTED_SENT_ITEMS = 8;

/**
 * Counts a hub group's scripts and briefs sent to a vendor. Hovering lists
 * them; `onPeek` loads the group's rows so a collapsed group can name them.
 */
export function OpportunityHubScriptSentBadge({
  items,
  isLoadingTitles,
  onPeek,
}: {
  items: readonly HubChromeScriptGroupSentItem[];
  isLoadingTitles: boolean;
  onPeek?: () => void;
}) {
  const { t } = useTranslation();
  if (items.length === 0) return null;

  const listed = items.slice(0, MAX_LISTED_SENT_ITEMS);
  const hiddenCount = items.length - listed.length;
  const isNaming = isLoadingTitles && listed.some((item) => item.title == null);

  return (
    <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
      <Tooltip onOpenChange={(open) => (open ? onPeek?.() : undefined)}>
        <TooltipTrigger asChild>
          <Badge
            variant="slate"
            iconStart="share"
            className="text-[11px] tabular-nums"
            data-testid="opportunity-hub-script-sent-badge"
          >
            {t("personaOpportunities.hub.scripts.sentCount", {
              count: items.length,
              defaultValue_one: "{{count}} sent",
              defaultValue_other: "{{count}} sent",
            })}
          </Badge>
        </TooltipTrigger>
        <TooltipContent align="end" className="max-w-80 px-3 py-2">
          <div className="flex flex-col gap-1.5" data-testid="opportunity-hub-script-sent-list">
            <p className="font-medium">
              {t("personaOpportunities.hub.scripts.sentListTitle", {
                count: items.length,
                defaultValue_one: "{{count}} sent to vendor",
                defaultValue_other: "{{count}} sent to vendor",
              })}
            </p>
            {isNaming ? (
              <div className="flex flex-col gap-1" aria-busy>
                {listed.map((item) => (
                  <Skeleton key={item.id} className="h-3.5 w-48" />
                ))}
              </div>
            ) : (
              <ul className="flex flex-col gap-0.5">
                {listed.map((item) => (
                  <li key={item.id} className="flex min-w-0 items-baseline gap-2">
                    <span className="truncate">
                      {item.title ??
                        t("personaOpportunities.hub.scripts.untitledScript", "Untitled script")}
                    </span>
                    {item.vendorName ? (
                      <span className="shrink-0 opacity-70">{item.vendorName}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {hiddenCount > 0 ? (
              <p className="opacity-70">
                {t("personaOpportunities.hub.scripts.sentListMore", {
                  count: hiddenCount,
                  defaultValue: "+{{count}} more",
                })}
              </p>
            ) : null}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
